import React, { useState, useEffect, useCallback, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { toast } from "react-toastify";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import { BASE_URL } from "../../../global";

const MultipleJobAssignButton = ({
  multipleJobAssign,
  setMultipleJobAssign,
  allUsers,
  setselectedSRfMultiple,
  setSearched,
  clearSearch,
  setSelectedDisciplineIds,
  setselectedCalStatus,
  selectedCalStatus,
}) => {
  const validCompanies = localStorage.getItem("validClients");
  const [certificate, setCertificate] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [SRFNoList, setSRFNoList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [uniqueServiceReqNumbers, setUniqueServiceReqNumbers] = useState([]);
  const [selectedServiceReqNumber, setSelectedServiceReqNumber] = useState(null);
  const [certificatesForServiceReq, setCertificatesForServiceReq] = useState([]);
  const [calType, setcalType] = useState(1);
  const [disciplines, setDisciplineList] = React.useState([]);
  const [calibrationStatus, setCalibrationStatus] = useState(null);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [jobNumberRange, setJobNumberRange] = useState({ from: "", to: "" });
  const userType = localStorage.getItem("type");
  const clientId = localStorage.getItem("clientId");

  const fetchCertificates = useCallback(async () => {
    if (!selectedServiceReqNumber) return;
    try {
      const { data } = await axiosWithToken.post(BASE_URL + `dynamic`, {
        query: `SELECT id,jobNumber,serviceReqNumber,calibrationType,discID, totalReadings, calibratedBy FROM certificatesView WHERE status != -1 and calibrationType = ${calType} and serviceReqNumber = '${selectedServiceReqNumber?.serviceReqNumber}'`,
      });
      setCertificate(data);
    } catch (err) {
      console.error("Data fetching error: ", err);
    }
  }, [selectedServiceReqNumber, calType]);

  const fetchClientList = useCallback(async () => {
    try {
      let url = BASE_URL + `clients?_where=(status,eq,1)`;
      // If user type is 3
      if (userType == "3") {
        url += `~and(id,eq,${clientId})`;
      }
      if (userType == 8 || userType == 2){
         if (validCompanies?.length > 0) {
          url += `~and(id,in,${validCompanies})`;
        }
      }
      const { data } = await axiosWithToken.get(url);
      setClientList(data);
    } catch (err) {
      console.error("Data fetching error: ", err);
    }
  }, []);

  const fetchSRFNoList = useCallback(async () => {
    try {
      const { data } = await axiosWithToken.get(
        BASE_URL + `srfs?_where=(status,eq,1)`,
      );
      setSRFNoList(data);
    } catch (err) {
      console.error("Data fetching error: ", err);
    }
  }, []);

  // Extract unique discipline IDs from certificates
  const uniqueDiscIDs = useMemo(() => {
    if (certificate.length === 0) return "";
    const ids = [...new Set(certificate.map(cert => cert.discID))].sort((a, b) => a - b);
    return ids.join(',');
  }, [certificate]);

  const getDisciplineList = useCallback(async () => {
    if (!selectedServiceReqNumber || !uniqueDiscIDs) return;
    try {
      const { data } = await axiosWithToken.post(BASE_URL + `dynamic`, {
        query: `SELECT id,name,disciplineKey FROM discipline WHERE status = 1 AND id IN (${uniqueDiscIDs})`,
      });
      setDisciplineList(data);
    } catch (err) {
      console.error("Data fetching error: ", err);
    }
  }, [selectedServiceReqNumber, uniqueDiscIDs]);

  useEffect(() => {
    fetchClientList();
    fetchSRFNoList();
  }, [fetchClientList, fetchSRFNoList]);

  useEffect(() => {
    fetchCertificates();
    getDisciplineList();
  }, [fetchCertificates, getDisciplineList]);
  
  useEffect(() => {
    const ids = selectedDisciplines.map(d => d.value);
    setSelectedDisciplineIds(ids);
  }, [selectedDisciplines]);

  useEffect(() => {
    if (selectedCompany) {
      const filtered = SRFNoList.filter(
        (item) => item.clientId === selectedCompany.id,
      );
      setUniqueServiceReqNumbers(filtered);
      setSelectedServiceReqNumber(null);
      setJobNumberRange({ from: "", to: "" });
    } else {
      setUniqueServiceReqNumbers([]);
    }
  }, [selectedCompany, SRFNoList]);

  useEffect(() => {
    if (selectedServiceReqNumber) {
      setSearched(true);
      setCertificatesForServiceReq(certificate);
      setJobNumberRange({ from: "", to: "" });
    } else {
      setCertificatesForServiceReq([]);
    }
  }, [selectedServiceReqNumber, certificate, setSearched]);

  const filteredCertificatesByDiscipline = useMemo(() => {
    if (selectedDisciplines.length === 0) {
      return certificatesForServiceReq;
    }
    const selectedDiscIds = selectedDisciplines.map(d => d.value);

    return certificatesForServiceReq.filter(cert =>
      selectedDiscIds.includes(cert.discID)
    );
  }, [certificatesForServiceReq, selectedDisciplines]);

  const filteredCertificatesByDisciplineAndStatus = useMemo(() => {
    let data = filteredCertificatesByDiscipline;

    if (calibrationStatus === "CALIBRATED") {
      data = data.filter(cert => {
        const hasReadings = cert.totalReadings !== null && cert.totalReadings > 0;
        const hasCalibratedBy = cert.calibratedBy !== null && 
                                cert.calibratedBy !== ""; 
        return hasReadings && hasCalibratedBy;
      });
    } else if (calibrationStatus === "NON_CALIBRATED") {
      data = data.filter(cert => {
        const noReadings = cert.totalReadings === null || cert.totalReadings == 0;
        const noCalibratedBy = cert.calibratedBy === null || 
                               cert.calibratedBy === "";
        return noReadings || noCalibratedBy;
      });
    }

    return data;
  }, [filteredCertificatesByDiscipline, calibrationStatus]);

  const jobNumbers = useMemo(
    () => filteredCertificatesByDisciplineAndStatus.map((cert) => cert.jobNumber),
    [filteredCertificatesByDisciplineAndStatus]
  );

  const sortedJobNumbersAsc = useMemo(
    () =>
      [...jobNumbers].sort(
        (a, b) => parseInt(a.split("/")[1]) - parseInt(b.split("/")[1]),
      ),
    [jobNumbers],
  );

  const sortedJobNumbersDesc = useMemo(
    () =>
      [...jobNumbers].sort(
        (a, b) => parseInt(b.split("/")[1]) - parseInt(a.split("/")[1]),
      ),
    [jobNumbers],
  );

  const isRangeValid = useCallback(
    (from, to) => {
      if (!from || !to) return false;
      const fromNumber = parseInt(from.split("/")[1]);
      const toNumber = parseInt(to.split("/")[1]);
      return (
        fromNumber <= toNumber &&
        filteredCertificatesByDisciplineAndStatus.some(
          (cert) =>
            parseInt(cert.jobNumber.split("/")[1]) >= fromNumber &&
            parseInt(cert.jobNumber.split("/")[1]) <= toNumber,
        )
      );
    },
    [filteredCertificatesByDisciplineAndStatus],
  );

  const getFilteredCertificatesByJobNumberRange = useCallback(
    (certificates, from, to) => {
      if (!from || !to) return [];
      const fromNumber = parseInt(from.split("/")[1]);
      const toNumber = parseInt(to.split("/")[1]);
      return certificates.filter(
        (item) =>
          parseInt(item.jobNumber.split("/")[1]) >= fromNumber &&
          parseInt(item.jobNumber.split("/")[1]) <= toNumber,
      );
    },
    [],
  );

  const saveUserAndDsID = useCallback(
    (userId) => {
      const selectedJobNumbers = getFilteredCertificatesByJobNumberRange(
        filteredCertificatesByDisciplineAndStatus,
        jobNumberRange.from,
        jobNumberRange.to,
      );

      const selectedIds = selectedJobNumbers.map((cert) => cert.id);
      if (selectedIds.length > 0) {
       const assignedUserValue = userId == 0 ? null : userId;
        const data = {
          query: `UPDATE datasheets SET assignedUser = ${assignedUserValue}, jobAssignedDate = NOW() WHERE id IN (${selectedIds});`,
        };
        axiosWithToken.post(BASE_URL + `dynamic`, data)
        .then(() => {
            toast("Datasheets Assigned.");
            window.location.reload();
          })
          .catch((err) => {
            console.error("Error updating datasheets: ", err);
          });
      }
    },
    [filteredCertificatesByDisciplineAndStatus, jobNumberRange, getFilteredCertificatesByJobNumberRange]
  );

  return (
    <div style={{ padding: "1rem 0" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <Autocomplete
            sx={{ m: 0, minWidth: "100%" }}
            size="small"
            className="Listinputcss"
            options={clientList}
            getOptionLabel={(option) => option.companyName + (option.uniCode ? ` (${option.uniCode})` : "")}
            value={selectedCompany}
            onChange={(event, newValue) => setSelectedCompany(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Company"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </Grid>

        {selectedCompany && (
          <Grid item xs={12} md={3}>
            <Autocomplete
              sx={{ m: 0, minWidth: "100%" }}
              size="small"
              className="Listinputcss"
              options={uniqueServiceReqNumbers}
              getOptionLabel={(option) => option.serviceReqNumber}
              value={selectedServiceReqNumber}
              onChange={(event, newValue) => {
                setSelectedServiceReqNumber(newValue);
                setselectedSRfMultiple(newValue?.serviceReqNumber);
                setcalType(newValue?.calType || 1);
                // Reset filters when SRF changes
                setSelectedDisciplines([]);
                setCalibrationStatus(null);
                setJobNumberRange({ from: "", to: "" });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select SRF"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>
        )}

        {selectedServiceReqNumber && (
          <>
            <Grid item xs={12} md={6}>
              <Autocomplete
                sx={{ m: 0, minWidth: "100%" }}
                size="small"
                className="Listinputcss"
                id="edit_stdMastersEQP_discipline"
                multiple
                options={[{ label: "Select All", value: 0 }, ...disciplines.map(d => ({ label: `${d.id}, ${d.name}`, value: d.id }))]}
                limitTags={1}
                value={selectedDisciplines}
                onChange={(e, newValue) => {
                  // Check if "Select All" was clicked
                  const selectAllClicked = newValue.some(o => o.value === 0) &&
                    !selectedDisciplines.some(o => o.value === 0);

                  // Check if we're deselecting "Select All"
                  const deselectAllClicked = !newValue.some(o => o.value === 0) &&
                    selectedDisciplines.length === disciplines.length;

                  if (selectAllClicked) {
                    // Select all disciplines (excluding "Select All" option)
                setSelectedDisciplines(
                      disciplines.map(d => ({ label: `${d.id}, ${d.name}`, value: d.id }))
                    );
                  } else if (deselectAllClicked) {
                    // Deselect all
                    setSelectedDisciplines([]);
                  } else {
                    // Normal selection (filter out "Select All" if present)
                    setSelectedDisciplines(newValue.filter(o => o.value !== 0));
                  }
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option, { selected }) => {
                  const isSelectAll = option.value === 0;
                  const allSelected = selectedDisciplines.length === disciplines.length;
                  const isChecked = isSelectAll ? allSelected : selected;

                  return (
                  <li {...props}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      readOnly
                      style={{ marginRight: 8 }}
                    />
                    {option.label}
                  </li>
                );
              }}
                renderInput={(params) => (<TextField {...params} label="Discipline *" sx={{ minHeight: 40 }}  size="small" 
                />)}
              />
            </Grid>

            <Grid item xs={12} />
            <Grid item xs={12} md={2}>
              <Autocomplete
                size="small"
                className="Listinputcss"
                options={[
                  { label: "Non-Calibrated", value: "NON_CALIBRATED" },
                  { label: "Calibrated", value: "CALIBRATED" },
                ]}
                value={
                  calibrationStatus
                    ? {
                      label:
                        calibrationStatus === "CALIBRATED"
                          ? "Calibrated"
                          : "Non-Calibrated",
                      value: calibrationStatus,
                    }
                    : null
                }
                onChange={(e, newValue) => {
                  setCalibrationStatus(newValue?.value || null);
                  setselectedCalStatus(newValue?.value || null);
                  setJobNumberRange({ from: "", to: "" });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Calibration Status" />
                )}
              />
            </Grid>
            </>
        )}
        {selectedCalStatus && (
            <>            
            <Grid item xs={12} md={2}>
              <Autocomplete
                sx={{ m: 0, minWidth: "100%" }}
                size="small"
                className="Listinputcss"
                options={sortedJobNumbersAsc}
                value={jobNumberRange.from}
                onChange={(event, newValue) =>
                  setJobNumberRange({ ...jobNumberRange, from: newValue })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From Job Number"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Autocomplete
                sx={{ m: 0, minWidth: "100%" }}
                size="small"
                className="Listinputcss"
                options={sortedJobNumbersDesc}
                value={jobNumberRange.to}
                onChange={(event, newValue) =>
                  setJobNumberRange({ ...jobNumberRange, to: newValue })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To Job Number"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
          </>
        )}

        {isRangeValid(jobNumberRange.from, jobNumberRange.to) && (
        <Grid item xs={12} md={2}>
            <Autocomplete
              sx={{
                m: 0,
                width: "100%",
              }}
              className="Listinputcss"
              size="small"
              id="combo-box-demo"
              options={[
                { label: "0, Withdraw Job" }, //  not assigned anyone
                ...allUsers,
              ]}
              getOptionLabel={(option) => option.label}              
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    color: option.label.startsWith("0, Withdraw Job") ? "red" : "inherit",
                  }}
                >
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Job Assign" />
              )}
              onInputChange={(event, newInputValue) => {
                saveUserAndDsID(parseInt(newInputValue.split(",")[0]));
              }}
            />
        </Grid>
        )}

        <Grid item xs={12} md={1}>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              clearSearch();
              setMultipleJobAssign(!multipleJobAssign);
            }}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default MultipleJobAssignButton;
